import { updateForm } from '@api/formsApi';
import { fromInternal, toInternal } from '@models/form-customization';
import { FormModel } from '@models/form-model';
import { Test, TestSimple } from '@models/test';
import { Button, Col, Form, Icon, Input, message, Row, notification } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { useMutation } from 'api-hooks/hooks';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import TestsDrawer from './TestsDrawer';

type Props = {
  onBack: () => any;
  onSave: () => any;
  formModel: FormModel;
  customerId: number;
} & FormComponentProps;

const drop = <T extends {}>(arr: T[], index: number) => {
  const a = [...arr];
  a.splice(index, 1);
  return a;
};

const TextArea: React.FC<any> = forwardRef((props, ref) => {
  // const { value, maxLength, id } = props;
  // const count = value ? value.length : 0;
  // const lines = (value && value.length > 0 && value.split(/\n/).length > 0) ? value.split(/\n/).length : 0;
  // const maxLines = id.endsWith('6')? 6:17;

  return (
    <>
      <Input.TextArea ref={ref} {...props} />
      NOTE: Number of characters and lines of text is limited and dependent upon Test  Codes selection. Alert message indicates when limit is exceeded.
      {/* {maxLength && (
        <>
          {count}/{maxLength} characters or {lines}/{maxLines} lines (Contingent upon test selection) 
        </>
      )} */}
    </>
  );
});

const CustomizationDetailsEdit: React.FC<Props> = ({
  onBack,
  onSave,
  form,
  formModel,
  customerId
}) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [vets, setVets] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [codes, setCodes] = useState<TestSimple[]>([]);
  const { getFieldDecorator } = form;
  const [update, { loading }] = useMutation(updateForm);
  const customs = useMemo(() => toInternal(formModel.form_customizations), [
    formModel
  ]);

  useEffect(() => {
    setVets(customs.veterinarians);
    setCodes(customs.codes);
  }, [customs]);

  const addVet = () => setVets(v => [...v, '']);
  const removeVet = (idx: number) => {
    const fv = form.getFieldValue('veterinarians');
    form.setFieldsValue({ veterinarians: drop(fv, idx) });
    setVets(v => drop(v, idx));
  };

  const onSubmit = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        if (codes.some(c => c.id == null)) {
          message.error('Remove all unavailable codes before saving');
          return;
        }
        const data = {
          ...formModel,
          form_customizations: fromInternal({
            veterinarians: values.veterinarians ?? [],
            notes: values.notes,
            codes: codes
          })
        };
        update(data, customerId).then(({ data, error }) => {
          if (data) {
            onSave();
          } else {
            message.error('Server error occurred');
            console.error(error);
          }
        });
      }
    });
  };

  const openNotificationWithIcon = () => {
    notification.warning({
      message: 'Characters limit reached',
      description:
        'You have selected the maximum number of tests that will fit on the form.',
    });
  };
  const onSelect = (test: Test) => {
    if (overLimit()) {
      openNotificationWithIcon();
    }
    setCodes(t => [
      ...t,
      { id: test.id, name: `${test.test_code} ${test.name}` }
    ]);
  }

  const onDeselect = (id: number) => {
    setCodes(t => {
      const idx = t.findIndex(x => x.id === id);
      if (idx !== -1) {
        t.splice(idx, 1);
      }
      return [...t];
    });
  }

  const updateMaxTextArea = () => {
    maxTextAreaLength = getMaxTextArea();
  }

  const updateMaxTextAreaLines = () => {
    maxCols = getMaxTextLines();
  }

  const overLimit = () => {
    let limit = formModel.name.startsWith("Equine")? 6 : 17;
    let noteLines;

    if (notes === '') {
      setNotes(customs.notes);
    }
    if (notes && notes.split(/\n/).length > 0) {
      noteLines = notes.split(/\n/).length;
    } else {
      noteLines = Math.round ( (notes? notes.length : 0)/66);
    }
    limit -= noteLines;
    return codes.length >= limit -1;
  }
  
  const getMaxTextLines = () => {
    let maxCols = formModel.name.startsWith("Equine")? 6 : 17;
    return maxCols - codes.length;
  }
  
  const getMaxTextArea = () => {
    const maxCharsPerLine = 66;
    if(formModel.name.startsWith("Equine")) {
      const maxLines = 6;
      if(codes.length >= maxLines) {
        return 0;
      } else {
        return (maxLines - codes.length) * maxCharsPerLine;
      }
    } else {
      const maxLines = 17;
      if(codes.length >= maxLines) {
        return 0;
      } else {
        return (maxLines - codes.length) * maxCharsPerLine;
      }
    }
  }

  let maxTextAreaLength = getMaxTextArea();
  let maxCols = getMaxTextLines();
  
  const removeIndex = (idx: number) =>
    setCodes(t => {
      if (idx !== -1) {
        t.splice(idx, 1);
      }
      updateMaxTextArea();
      updateMaxTextAreaLines()
      overLimit();
      return [...t];
    });
    
  return (
    <Form onSubmit={onSubmit} layout="vertical">
      <TestsDrawer
        selectedRowKeys={codes.filter(t => t.id != null).map(t => t.id || -1)}
        onSelect={onSelect}
        onDeselect={onDeselect}
        visible={drawerVisible}
        formName={formModel.name}
        notesText={notes}
        onClose={() => setDrawerVisible(false)}
      />
      <Form.Item label="Veterinarians">
        {vets.map((v, i) => (
          <Form.Item key={i} className="CustomizationDetails-vet-input">
            {getFieldDecorator(`veterinarians[${i}]`, {
              rules: [
                {
                  required: true,
                  whitespace: true,
                  message:
                    "Please input veterinarian's name or delete this field."
                }
              ],
              initialValue: v
            })(<Input placeholder="Veterinarian's name" />)}
            <Icon
              type="minus-circle"
              className="text-primary"
              theme="filled"
              onClick={() => removeVet(i)}
            />
          </Form.Item>
        ))}
        <Button type="primary" className="btn-secondary" onClick={addVet}>
          Add New Veterinarian
        </Button>
      </Form.Item>
      <Form.Item label="Special Test Request Notes">
        {getFieldDecorator('notes', { initialValue: customs.notes })(
          <TextArea onChange={e => {
            if (e.target.value.split(/\n/).length > maxCols) {
              e.preventDefault();
              e.target.maxLength = e.target.value.split(/\n/).join('').length;
              maxTextAreaLength = e.target.maxLength;
              setNotes(e.target.value);
            } else {
              setNotes(e.target.value);
              e.target.maxLength = getMaxTextArea();
              maxTextAreaLength = e.target.maxLength;
            }
          }} rows={4} maxLength={maxTextAreaLength} placeholder="Type notes" />
        )}
      </Form.Item>
      <Form.Item
        label={
          <div className="CustomizationDetails-codes-header">
            Special Test Request Test Codes
            <Button
              type="primary"
              className="btn-secondary"
              onClick={() => {setDrawerVisible(true)}}>
              Select Test Codes
            </Button>
          </div>
        }>
        {codes.map((t, i) => (
          <Row key={t.name} type="flex" className="CustomizationDetails-codes">
            <Col span={22}>
              {t.name} {t.id == null && '(unavailable)'}
            </Col>
            <Col span={2} className="CustomizationDetails-codes-icon">
              <Icon
                type="minus-circle"
                className="text-primary"
                theme="filled"
                onClick={() => removeIndex(i)}
              />
            </Col>
          </Row>
        ))}
        {codes.length === 0 && <div>No codes</div>}
      </Form.Item>
      <div className="btn-group">
        <Button onClick={onBack}>Cancel</Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          Save
        </Button>
      </div>
    </Form>
  );
};

export default Form.create<Props>()(CustomizationDetailsEdit);
